import { Alert, Button, Dialog, Grid, Snackbar } from '@mui/material';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { SelectWorkOrders } from './select-workorders.component';
import { SelectInspector } from './select-inspector.component';
import { DateFormat, FormDateField } from '../../components/hook-form/form-date.field.component';
import { FormCheckbox } from '../../components/hook-form/form-checkbox.component';
import { useSaveData, useSaveDataForFastField } from '../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { GuardedComponent } from '../../components/guarded-component';
import { UserPermissionName } from '../../models/user-permission-name';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { WorkOrder } from '../../models/workOrder';
import { useSearchParams } from 'react-router-dom';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAuthorize } from '../../hooks/use-authorize';
import { watch } from 'fs';
import { holidayManagementApiKeyStore } from '../../api/masters/holiday-management-api-key-store';
import { useSnackbar } from '../../hooks/use-snackbar.hook';

const styling = {
  root: { paddingBottom: '10px' },
  buttonStyle: { width: '130px' },
};

const today = dayjs().startOf('day');

const bulkShedulingSchema = Yup.object().shape({
  // keepScheduledDateasOriginal: Yup.boolean().required('Keep original date or Scheduled date is required '),

  // Basic validation for 'scheduledDate'
  scheduledDate: Yup.date()

    .typeError('Please enter a valid date') // Ensures the date is valid
    .min(today, 'Schedule date must be in the future'),

  // inspectorID: Yup.number()
  //   .required('Please select an inspector') // Required field
  //   .min(1, 'Please select an inspector'),
});

const convertToArray = (type: string, number: any) => {
  let returnArray: any[] = [];
  if (number) {
    const ids = number.toString().split(',');
    if (type == 'office')
      returnArray = ids.map((id: any) => ({
        officeID: parseInt(id.trim()),
        officeName: '',
      }));
    if (type == 'project')
      returnArray = ids.map((id: any) => ({
        projectID: parseInt(id.trim()),
        projectName: '',
      }));
    if (type == 'marketArea')
      returnArray = ids.map((id: any) => ({
        marketAreaID: parseInt(id.trim()),
        marketAreaName: '',
      }));
    if (type == 'inspector')
      returnArray = ids.map((id: any) => ({
        userID: parseInt(id.trim()),
        fullName: '',
      }));
    if (type == 'devision')
      returnArray = ids.map((id: any) => ({
        divisionID: parseInt(id.trim()),
        divisionName: '',
      }));
  }

  return returnArray;
};

export const BulkScheduling = () => {
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState({ severity: 'success', messageInfo: '' });
  const [pendingErrorRules, setPendingErrorRoles] = useState([]);
  const [workOrderForWarnings, setWorkOrderForWarnings] = useState([]);
  const [openDialogForDate, setOpenDialogForDate] = useState(false);
  const [openDialogForRules, setOpenDialogForRules] = useState(false);
  const ishavePermission = useAuthorize([UserPermissionName?.OVERRIDE]);
  const [selectedWorkorder, setSelectedWorkorder] = useState<WorkOrder[]>([]);
  const [commonInspector, setCommonInspector] = useState<any>();
  const [selectedInspector, setSelectedInspector] = useState<any[]>([]);
  const [isReCall, setIsRecall] = useState(false);

  const { isLoading, mutateAsync } = useSaveData<any>(workOrdersApiKeyStore.bulkSchedule());

  const { data: statusData, isLoading: isStatusLoading } = useLoadData<ApiResponse<any>>(
    workOrdersApiKeyStore.getWorkOrderStatus('BulkscheduleInspectionListFilter')
  );

  const [holidayDates, setHolidayDates] = useState<any[]>([]);
  const { isLoading: isHolidayLoading, data: holidayData } = useLoadData<ApiResponse<any[]>>(
    holidayManagementApiKeyStore.getHolidays());

  useEffect(() => {
    if (holidayData) {
      const datesResult = holidayData?.data
        .filter((item: any) => item.isActive)
        //?.map((item: any) => item.holidayDate);
        ?.map((item: any) => ({
          date: item.holidayDate,
          name: item.holidayName,
        }));
      setHolidayDates(datesResult);
    }
  }, [holidayData])

  const filterData = {
    customer: Number(searchParams.get('customerID')),
    officeIDs: convertToArray('office', searchParams.get('offices')),
    projectIDs: convertToArray('project', searchParams.get('projects')),
    marketAreaIDs: convertToArray('marketArea', searchParams.get('marketAreas')),
    userIDs: convertToArray('inspector', searchParams.get('inspectors')),
    workOrderStatusID: Number(searchParams.get('workOrderStatusID')),
    fromDate: searchParams.get('dateFrom') ?? '',
    toDate: searchParams.get('dateTo') ?? '',
    divisions: convertToArray('devision', searchParams.get('divisions')),
  };

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: filterData.customer ?? '', customerName: '' },
      officeIDs: filterData.officeIDs,
      projectIDs: filterData.projectIDs,
      marketAreaIDs: filterData.marketAreaIDs,
      userIDs: filterData.userIDs,
      workOrderStatusID: { workOrderStatusID: 0, workOrderStatus: '' },
      fromDate: filterData.fromDate,
      toDate: filterData.toDate,
      divisions: filterData.divisions,
      workOrders: [],
      keepScheduledDateasOriginal: false,
    },
    yupSchema: bulkShedulingSchema,
  });

  const childRef = useRef<any>(null);
  const watchValues = form.watch();

  const { isLoading: isLoadingFastfieldDispatch, mutateAsync: fastfieldDispatch } = useSaveDataForFastField(
    workOrdersApiKeyStore.fastFieldDispatch(
      watchValues?.workOrders?.map((data: WorkOrder) => data?.workOrderID),
      'bulk'
    )
  );

  const { isLoading: sheduleRuleLoading, mutateAsync: sheduleRuleCheck } = useSaveData<any>(
    workOrdersApiKeyStore.validateWorkOrderSheduleRules()
  );

  useEffect(() => {
    if (statusData) {
      const targetObject = statusData?.data?.find(
        (item: any) => item.workOrderStatusID === filterData?.workOrderStatusID
      );
      form.reset({
        workOrderStatusID: {
          workOrderStatusID: targetObject ? targetObject?.workOrderStatusID : 2,
          workOrderStatus: targetObject ? targetObject?.workOrderStatus : 'Ready',
        },
      });
    }
  }, [statusData]);

  useEffect(() => {
    if (watchValues?.keepScheduledDateasOriginal) {
      form.resetField('scheduledDate', { defaultValue: undefined });
    }
  }, [watchValues?.keepScheduledDateasOriginal]);

  const addPendingRule = (workOrder: any, pendingRulesMessages: any, message: any) => {
    const existingEntry = pendingRulesMessages?.find((ruleMessage: any) => ruleMessage.workOrderName === workOrder);

    if (existingEntry) {
      // If it exists, add the message to its pendingRules
      existingEntry.pendingRules.push(message);
    } else {
      // If it does not exist, add a new entry with the message
      pendingRulesMessages.push({
        workOrderName: workOrder,
        pendingRules: [message],
      });
    }
  };

  // Helper function to process warnings and set states
  const processWarnings = (workOrderForWarnings: any, pendingRulesMessages: any, message: any) => {
    setWorkOrderForWarnings(workOrderForWarnings);

    workOrderForWarnings.forEach((workOrder: any) => {
      addPendingRule(workOrder, pendingRulesMessages, message);
    });

    setPendingErrorRoles(pendingRulesMessages);
    setOpenDialogForDate(true);
  };

  const handleSubmit = async (data: any) => { };
  const watchValue = form.watch();
  const handleDirectApiCall = async (data: any) => {
    await mutateAsync({
      data: {
        ...data,
        scheduledDate: dayjs(data?.scheduledDate).format('MM/DD/YYYY'),
        workOrders: data.workOrders ? data.workOrders.map((p: any) => p.workOrderID) : [],
        ScheduleFromArea: 'Bulk Scheduling',
        inspectorID: selectedInspector?.length > 0 ? selectedInspector[0]?.userID : data?.inspectorID
      },
    });
    setMessage({ severity: 'success', messageInfo: 'Bulk scheduling completed successfully!' });
    setOpen(true);
    setWorkOrderForWarnings([]);
    setPendingErrorRoles([]);
    setOpenDialogForDate(false);
    setOpenDialogForRules(false);
    if (childRef.current) {
      childRef.current.handleFilterAutoClick();

      setIsRecall(true);
      form.resetField('scheduledDate', { defaultValue: undefined });
      form.setValue('keepScheduledDateasOriginal', false);
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    // setMessage({ severity: '', messageInfo: '' });
    setOpen(false);
  };

  const closeDialog = () => {
    setOpenDialogForDate(false);
    setPendingErrorRoles([]);
  };

  const handleSumbitConfirm = async () => {
    handleDirectApiCall(watchValue);
    setOpenDialogForDate(false);
  };

  const getInspectorInfo = (data: any) => {
    if (!data || data.length === 0) return null;

    const firstInspector = data[0].inspectorName;

    // Check if inspectorName is valid and same for all
    const allSame = data?.every(
      (item: any) =>
        item.inspectorName &&
        item.inspectorName.trim() !== "" &&
        item.inspectorName === firstInspector
    );

    if (allSame) {
      // If any one false -> whole result false
      const isAssigned = data?.every((item: any) => item.isInspectorAssignedOnReady === true);

      return {
        inspectorName: firstInspector,
        isInspectorAssignedOnReady: isAssigned, // true only if all are true
      };
    }
    return null;
  }

  const handleSelectedWorkOrder = (value: WorkOrder[]) => {
    setSelectedWorkorder(value);
    const returnInfo = getInspectorInfo(value ?? []);
    setCommonInspector(returnInfo);
    setSelectedInspector((prev) => (prev.length ? [] : prev));
  };

  const handleSelectedInspector = (value: WorkOrder[]) => {
    setSelectedInspector(value);
  };

  const validateUnassignedInspections = (insData: any[]) => {
    const hasUnassigned = insData?.some(
      (item) => !item.inspectorName || item.inspectorName.trim() === ""
    );

    if (hasUnassigned) {
      const readinessValues = Array.from(
        new Set(insData?.map((item) => item.isInspectorAssignedOnReady))
      );

      const hasBothTrueAndFalse =
        readinessValues.includes(true) && readinessValues.includes(false);

      if (hasBothTrueAndFalse) {
        return "unassigned inspections";
      }
    }

    return "";
  };


  const validateInspectorName = (insData: any) => {
    if (insData?.length > 0) {
      const firstName = insData[0]?.inspectorName;
      const allSameName = insData?.every((item: any) => item.inspectorName === firstName);

      if (!allSameName) return "not match inspections";
    }
    return "";
  }

  const getInvalidWorkorders = (selectedWorkorder: any, holidayDates: any) => {
    if (!selectedWorkorder?.length) return [];

    return selectedWorkorder?.filter((item: any) => {
      //const dateStr = item?.proposedScheduledDate;

     const dateStr = item?.workOrderStatus == "Ready" ? item?.proposedScheduledDate :
            (item?.workOrderStatus == "Scheduled" && !item?.requestedRescheduledDate) ? item?.scheduledDate :
              (item?.workOrderStatus == "Scheduled" && item?.requestedRescheduledDate) && item?.proposedScheduledDate;
          

      if (!dateStr) return false;

      const date = dayjs(dateStr, "MM/DD/YYYY");

      const isWeekend = date.day() === 0 || date.day() === 6;
      const isHoliday = holidayDates?.some(
        (holiday:any) => holiday.date === dateStr
);
      return isWeekend || isHoliday;
    });
  };

  const isHolidayOrWeekend = (scheduleDate: any, holidayDates: any) => {
    const selectedDate = dayjs(scheduleDate, "MM/DD/YYYY");

    // Check weekend
    const day = selectedDate.day(); // 0 = Sunday, 6 = Saturday
    const isWeekend = day === 0 || day === 6;

    // Check holiday
    const isHoliday = holidayDates?.some((holiday: any) =>
      dayjs(holiday.date, "MM/DD/YYYY").isSame(selectedDate, "day")
    );

    return {
      isWeekend,
      isHoliday,
      isInvalid: isWeekend || isHoliday,
    };
  };

  const handleSubmit1 = async (data: any) => {
    const hasProposedScheduledDate = data?.workOrders?.some((order: any) => order.proposedScheduledDate === null);
    const scheduleMessage = 'The schedule time is greater than cut off time';

    const allAssigned = selectedWorkorder?.every((item: any) => item?.isInspectorAssignedOnReady === true);

    const invalidWorkorders = getInvalidWorkorders(selectedWorkorder, holidayDates);

    const result = isHolidayOrWeekend(dayjs(data?.scheduledDate)?.format('MM/DD/YYYY'), holidayDates);

    if (hasProposedScheduledDate && !data?.keepScheduledDateasOriginal) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({
        severity: 'error',
        messageInfo: 'The selected inspection list includes inspections with no proposed schedule date.',
      });
      return;
    } else if (!data?.keepScheduledDateasOriginal && !data?.scheduledDate) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Keep original date or schedule date is required' });
      return;
    } else if (!data?.workOrders || (data?.workOrders && data?.workOrders?.length === 0)) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Please select workorder' });
      return;
    }
    else if (validateUnassignedInspections(selectedWorkorder) == "unassigned inspections") {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({
        severity: 'error',
        messageInfo: "Please select an inspector or update your selection. " +
          "Inspector preselection isn’t possible because your selection includes both assigned and unassigned inspections."
      });
      return;
    }
    else if (allAssigned && validateInspectorName(selectedWorkorder) == 'not match inspections') {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({
        severity: 'error',
        messageInfo: "Please select an inspector or update your selection. " +
          "Inspector preselection isn’t possible because the selected inspections are assigned to different inspectors."
      });
      return;
    }
    else if (!selectedInspector[0]?.userID) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Please select inspector' });
      return;
    }
    else if (data?.keepScheduledDateasOriginal && data?.scheduledDate) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Please select either keep original date or schedule date' });
      return;
    }
    else if (data?.remoteInspection && !data?.secondaryInspectorID) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Please select secondary inspector.' });
      return;
    }
    else if (data?.keepScheduledDateasOriginal && invalidWorkorders?.length > 0) {
      const workOrderNames = invalidWorkorders
        ?.map((item: any) => item.workOrderNumber)
        ?.join(", ");
      const isMultiple = invalidWorkorders?.length > 1;

      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({
        severity: 'error',
        messageInfo: `Selected work ${isMultiple ? "orders" : "order"} (${workOrderNames}) fall on non-working days (weekends or holidays). Please choose a valid working day to proceed.`
      });
      return;
    }
    // else if (result?.isHoliday && shouldRestrictDateSelection) {
    //   setOpen(true);
    //   setPendingErrorRoles([]);
    //   setMessage({ severity: 'error', messageInfo: 'The selected schedule date falls on a holiday. Please choose a valid working day to proceed.' });
    //   return;
    // }
    // else if (result?.isWeekend && shouldRestrictDateSelection) {
    //   setOpen(true);
    //   setPendingErrorRoles([]);
    //   setMessage({ severity: 'error', messageInfo: 'The selected schedule date falls on a weekend. Please choose a valid working day to proceed.' });
    //   return;
    // }

    const rulePayload = {
      workOrderInfos: data?.workOrders?.map((item: any) => ({
        workOrderID: item.workOrderID,
        workOrderNumber: item.workOrderNumber,
        scheduledDate: data?.keepScheduledDateasOriginal ?
          item?.workOrderStatus == "Ready" ? item?.proposedScheduledDate :
            (item?.workOrderStatus == "Scheduled" && !item?.requestedRescheduledDate) ? item?.scheduledDate :
              (item?.workOrderStatus == "Scheduled" && item?.requestedRescheduledDate) && item?.proposedScheduledDate
          : dayjs(data?.scheduledDate)?.format('MM/DD/YYYY'),
      }))
    };

    const ruleResponse: any = await sheduleRuleCheck({ data: rulePayload });

    const pendingRuleResponse = ruleResponse?.data?.map((item: any) => ({
      workOrderName: item.workOrderNumber,
      pendingRules: item.pendingRules,
      preReqWorkOrders: item?.preReqWorkOrders
    }));


    const pendingRulesMessages = pendingRuleResponse;

    if (data?.keepScheduledDateasOriginal) {
      const proposedScheduleDateWarnings: string[] = [];
      const scheduleDateWarnings: string[] = [];
      const requestScheduleDateWarnings: string[] = [];

      data?.workOrders?.forEach((workOrder: any) => {
        const { workOrderStatus, proposedScheduledDate, scheduledDate, requestedRescheduledDate, workOrderNumber } =
          workOrder;

        // Check for past proposed schedule date
        if (workOrderStatus === 'Ready' && dayjs(proposedScheduledDate).isBefore(dayjs().startOf('day'))) {
          proposedScheduleDateWarnings.push(workOrderNumber);
        }

        // Check for scheduled work orders
        if (workOrderStatus === 'Scheduled') {
          if (!requestedRescheduledDate && dayjs(scheduledDate).isBefore(dayjs().startOf('day'))) {
            scheduleDateWarnings.push(workOrderNumber);
          } else if (requestedRescheduledDate && dayjs(requestedRescheduledDate).isBefore(dayjs().startOf('day'))) {
            requestScheduleDateWarnings.push(workOrderNumber);
          }
        }
      });

      const showErrorMessage = (workOrderNumbers: string[], message: string) => {
        setOpen(true);
        setPendingErrorRoles([]);
        setMessage({
          severity: 'error',
          messageInfo: `Selected work order ${workOrderNumbers.join(', ')} ${message}`,
        });
      };

      if (
        proposedScheduleDateWarnings?.length > 0 ||
        scheduleDateWarnings?.length > 0 ||
        requestScheduleDateWarnings?.length > 0
      ) {
        if (proposedScheduleDateWarnings.length > 0) {
          showErrorMessage(proposedScheduleDateWarnings, 'have past proposed schedule date');
        } else if (scheduleDateWarnings.length > 0) {
          showErrorMessage(scheduleDateWarnings, 'have past schedule date');
        } else if (requestScheduleDateWarnings.length > 0) {
          showErrorMessage(requestScheduleDateWarnings, 'have past requested reschedule date');
        }
      } else {
        // When `keepScheduledDateasOriginal` is true
        const workOrderForWarnings = data?.workOrders
          ?.filter((order: any) => {
            const scheduledDate = dayjs(order.scheduledDate);
            const cutOffTime = order.cutOffTime ? dayjs(order.cutOffTime, 'hh:mm A') : null;
            const proposedScheduledDate = order.proposedScheduledDate ? dayjs(order.proposedScheduledDate) : null;
            const now = dayjs().startOf('minute');

            if (order.workOrderStatus === 'Scheduled' && scheduledDate.isSame(dayjs(), 'day')) {
              return cutOffTime && cutOffTime.isBefore(dayjs());
            } else if (
              order.workOrderStatus === 'Ready' &&
              proposedScheduledDate &&
              proposedScheduledDate.isSame(dayjs(), 'day')
            ) {
              return cutOffTime && cutOffTime.isBefore(dayjs());
            }
            return false;
          })
          .map((order: any) => order.workOrderNumber);

        if (workOrderForWarnings?.length > 0 || pendingRulesMessages?.length > 0) {
          if (workOrderForWarnings?.length > 0) {
            processWarnings(workOrderForWarnings, pendingRulesMessages, scheduleMessage);
          } else {
            setWorkOrderForWarnings([]);
            setPendingErrorRoles(pendingRulesMessages);
            setOpenDialogForDate(true);
          }
        } else {
          handleDirectApiCall(data);
        }
      }
    } else if (!data?.keepScheduledDateasOriginal) {
      // When `keepScheduledDateasOriginal` is false, undefined, or null
      const workOrderForWarnings = data?.workOrders
        ?.filter((order: any) => {
          const scheduledDate = dayjs(data?.scheduledDate);

          const cutOffTime = order.cutOffTime ? dayjs(order.cutOffTime, 'hh:mm A') : null;
          return scheduledDate.isSame(dayjs(), 'day') && cutOffTime && cutOffTime.isBefore(dayjs());
        })
        .map((order: any) => order.workOrderNumber);

      if (workOrderForWarnings?.length > 0 || pendingRulesMessages?.length > 0) {
        if (workOrderForWarnings?.length > 0) {
          processWarnings(workOrderForWarnings, pendingRulesMessages, scheduleMessage);
        } else {
          setWorkOrderForWarnings([]);
          setPendingErrorRoles(pendingRulesMessages);
          setOpenDialogForDate(true);
        }
      } else {
        handleDirectApiCall(data);
      }
    } else {
      if (pendingRulesMessages?.length > 0) {
        setWorkOrderForWarnings([]);
        setPendingErrorRoles(pendingRulesMessages);
        setOpenDialogForDate(true);
      } else {
        handleDirectApiCall(data);
      }
    }
  };

  const handleDateChanges = (date: any) => {
    if (date !== null || date !== '') {
      form.setValue('keepScheduledDateasOriginal', false);
    }
  }

  const handleSheduleClick = () => {
    handleSubmit1(watchValue);
  };

  //const isWeekendHolidaySchedulingOverridePermission = !useAuthorize([UserPermissionName?.WEEKEND_HOLIDAY_SCHEDULING_OVERRIDE]);
  const hasWeekendHolidayOverridePermission = useAuthorize([
    UserPermissionName.WEEKEND_HOLIDAY_SCHEDULING_OVERRIDE,
  ]);

  const shouldRestrictDateSelection = !hasWeekendHolidayOverridePermission;

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <ReactHookForm formName="BULK_SCHEDULING_FORM" onSubmit={handleSubmit} formContextProps={form} isBusy={isLoading}>
        <div>
          <Grid container spacing={1.5} style={{ background: 'none', padding: '0' }}>
            <Grid item lg={12} sm={12} xl={12} xs={12} style={{ paddingLeft: '16px', paddingTop: '24px' }}>
              <SelectWorkOrders
                selectedFilters={filterData}
                ref={childRef}
                selectedWorkorder={selectedWorkorder}
                handleSelectedWorkOrder={handleSelectedWorkOrder}
                selectedInspector={selectedInspector}
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <SelectInspector
                selectedWorkorder={selectedWorkorder}
                handleSelectedInspectors={handleSelectedInspector}
                selectedInspector={selectedInspector}
                isRecall={isReCall}
                commonInspector={commonInspector}
                handleResetRecall={() => setIsRecall(false)}
              />
            </Grid>
            <Grid item lg={12}>
              <LoadingIndicator isLoading={isLoading} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12} className="d-flex" style={{ justifyContent: 'flex-end' }}>
              <div style={{ marginRight: 'auto', paddingLeft: '16px' }}>
                <p style={{ marginBottom: '25px', lineHeight: '20px' }}>
                  <strong>Note:</strong>
                  <i>
                    <ul>
                      <li style={{ listStyle: 'none' }}>
                        <strong> If you select the `Keep original date` option for scheduling,</strong>{' '}
                      </li>

                      <li style={{ marginLeft: '16px' }}>
                        For inspections in ready status - The proposed schedule date will be the schedule date
                      </li>
                      <li style={{ marginLeft: '16px' }}>
                        For inspections in schedule status(reschedule)- the requested reschedule date will be the
                        schedule date/ the schedule date will be the same.
                      </li>
                      <li style={{ listStyle: 'none' }}>
                        <strong>If you select the `Schedule date` option for scheduling,</strong>
                      </li>
                      <li style={{ marginLeft: '16px' }}>
                        The selected schedule date will be set as the inspection`s schedule date.
                      </li>
                    </ul>
                  </i>
                </p>
              </div>

              <div style={{ maxWidth: '400px', display: 'flex', gap: '10', marginRight: '32px' }}>
                <div style={{ flex: '1' }}>
                  <FormCheckbox label="Keep original date" name="keepScheduledDateasOriginal" />
                </div>
                <div style={{ flex: '1' }}>
                  <FormDateField
                    label="Schedule Date"
                    name="scheduledDate"
                    dateLimits={{ minDateEnabled: true }}
                    onChange={handleDateChanges}
                    //restrictDateSelection={isWeekendHolidaySchedulingOverridePermission}
                    //holidayDateList={isWeekendHolidaySchedulingOverridePermission ? holidayDates : []}
                    restrictDateSelection={shouldRestrictDateSelection}
                    holidayDateList={holidayDates}
                    enableDateConfirmation={hasWeekendHolidayOverridePermission}
                    isParentLoading={isHolidayLoading}
                    disabled={isHolidayLoading}
                  />
                </div>
              </div>
              <div>
                <GuardedComponent requiredPermissions={[UserPermissionName.BULKSCHEDULE_INSPECTIONS]}>
                  <Button
                    type={'button'}
                    disabled={isLoading || sheduleRuleLoading || isReadOnlyPermission}
                    variant="contained"
                    disableElevation={true}
                    style={styling.buttonStyle}
                    onClick={() => handleSheduleClick()}
                  >
                    SCHEDULE
                  </Button>
                </GuardedComponent>
              </div>
            </Grid>
          </Grid>
          <Snackbar
            open={open}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            onClose={handleClose}
          >
            {message.severity === 'success' ? (
              <Alert severity="success" variant="filled" sx={{ width: '100%' }} onClose={handleClose}>
                {!message.messageInfo ? 'Bulk scheduling completed successfully!' : message?.messageInfo}
              </Alert>
            ) : (
              <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={handleClose}>
                {pendingErrorRules?.length > 0 ? (
                  <ul>
                    {pendingErrorRules.map((errorRule: any, index: number) => (
                      <li key={index}>
                        {errorRule.workOrderName}: {errorRule.pendingRules.join(', ')}
                      </li>
                    ))}
                  </ul>
                ) : !message.messageInfo ? (
                  ''
                ) : (
                  message?.messageInfo
                )}
              </Alert>
            )}
          </Snackbar>
        </div>
      </ReactHookForm>
      {openDialogForDate && (
        <Dialog
          open={openDialogForDate}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                <h4>Please see the below validations</h4>
                <br></br>
                {pendingErrorRules?.length > 0 ? (
                  <>
                    <ul>
                      {pendingErrorRules.map((data: any, index: number) => (
                        <li key={index}>
                          <ul>
                            <span style={{ color: 'orangered' }}>{data?.workOrderName}</span>
                            {data?.pendingRules?.map((errorRule: any) => (
                              <>
                                <li style={{ marginLeft: '25px' }} key={errorRule}>
                                  <span>{errorRule}</span>
                                </li>
                                {errorRule.includes("pre-requisite") && data?.preReqWorkOrders?.length > 0 && (
                                  <dl style={{ paddingLeft: 25 }}>
                                    <dt>Pending Work Orders:</dt>
                                    {data?.preReqWorkOrders?.map((data: any) => <dd key={data}>- {data}</dd>)}
                                  </dl>
                                )}
                              </>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                    <br></br>
                    <div>
                      {/* {ishavePermission ? (
                        <h5>Are you sure you want to continue?</h5>
                      ) : (
                        <h5>You do not have permission to schedule</h5>
                      )} */}
                      {ishavePermission ? (
                        <h5>
                          You have the permission to schedule without completing the validations. Are you sure you want
                          to continue?
                        </h5>
                      ) : (
                        <h5>You can not schedule without completing the validations.</h5>
                      )}
                    </div>
                  </>
                ) : (
                  ''
                )}

                {/* {ishavePermission
                  ? `The schedule time is greater than Cut off Time for workorder ${workOrderForWarnings.join(', ')}. Are you sure you want to continue?`
                  : `The schedule time is greater than Cut off Time for this workorder ${workOrderForWarnings.join(', ')}. You do not have permission to schedule.`} */}

                {/* {ishavePermission ? (
                  pendingErrorRules?.length > 0 ? (
                    <ul>
                      {pendingErrorRules.map((errorRule: any, index: number) => (
                        <li key={index}>
                          {errorRule.workOrderName}: {errorRule.pendingRules.join(', ')}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    ''
                  )
                ) : pendingErrorRules?.length > 0 ? (
                  <>
                    <ul>
                      {pendingErrorRules.map((errorRule: any, index: number) => (
                        <li key={index}>
                          {errorRule.workOrderName}: {errorRule.pendingRules.join(', ')}
                        </li>
                      ))}
                    </ul>
                    You do not have permission to schedule
                  </>
                ) : (
                  ''
                )}
                {ishavePermission ? (
                  workOrderForWarnings?.length > 0 ? (
                    <ul>
                      <li>
                        The schedule time is greater than Cut off Time for workorder {workOrderForWarnings.join(', ')}
                      </li>
                    </ul>
                  ) : (
                    ''
                  )
                ) : workOrderForWarnings?.length > 0 ? (
                  <ul>
                    <li>
                      The schedule time is greater than Cut off Time for this workorder{' '}
                      {workOrderForWarnings.join(', ')}. You do not have permission to schedule.
                    </li>
                  </ul>
                ) : (
                  ''
                )}
                {(workOrderForWarnings?.length > 0 || workOrderForWarnings?.length > 0) && ishavePermission
                  ? 'Do you want to continue?'
                  : ''} */}
              </div>
              <LoadingIndicator isLoading={isLoading} />
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeDialog}
                autoFocus
              >
                Cancel
              </button>
              {ishavePermission && (
                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                    color: isReadOnlyPermission ? 'black' : '#fff',
                    cursor: 'pointer'
                  }}
                  type="button"
                  onClick={handleSumbitConfirm}
                  disabled={isLoading || isReadOnlyPermission}
                >
                  Confirm
                </button>
              )}
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};
